/* ============================================================================
   Live price feed.

   Emits two things on every update:
     quotes  what the top rail renders (SOL, ETH, BASE)
     marks   raw USD prices per symbol, which the simulated round uses to anchor
             its own markets so the tape and the rail can never disagree

   Majors come from Binance's public combined stream (keyless, ~1 msg/sec), with
   an automatic fallback to Coinbase polling when Binance is unreachable -- it is
   geo-blocked in some regions and a blank rail is not an acceptable outcome.

   Base has no native token -- it settles in ETH -- so it gets no rail slot. Its
   tokens are still priced off DexScreener, because the simulated round anchors
   its Base markets to those real prices even though nothing there is quotable
   at the top of the screen.

   Everything here is replaceable: VITE_PRICE_FEED=custom points the whole rail
   at your own endpoint.
   ========================================================================== */

import { BASE_LIVE_TOKENS, PRICE_RAIL } from '../../config/competition'
import { RUNTIME } from '../../config/runtime'
import type { Quote } from '../../core/types'

export interface PriceUpdate {
  quotes: Quote[]
  /** symbol -> USD price, for every asset the feed actually knows about. */
  marks: Record<string, number>
  source: string
}

export interface PriceFeed {
  start(onUpdate: (u: PriceUpdate) => void): void
  stop(): void
}

const BINANCE_REST = 'https://api.binance.com/api/v3'
const BINANCE_WS = 'wss://stream.binance.com:9443/stream'
const COINBASE_REST = 'https://api.exchange.coinbase.com'
const DEXSCREENER = 'https://api.dexscreener.com/latest/dex/search'

const MAJORS = [
  { symbol: 'SOL', binance: 'SOLUSDT', coinbase: 'SOL-USD' },
  { symbol: 'ETH', binance: 'ETHUSDT', coinbase: 'ETH-USD' },
] as const

const railOf = (symbol: string) => PRICE_RAIL.find((r) => r.symbol === symbol)!

function quoteOf(
  symbol: string,
  price: number,
  open: number,
  high: number,
  low: number,
  spark: number[],
  note?: string,
): Quote {
  const rail = railOf(symbol)
  return {
    chain: rail.chain,
    symbol,
    label: rail.label,
    note: note ?? rail.note,
    price,
    change24hPct: open > 0 ? (price / open - 1) * 100 : 0,
    high24h: high,
    low24h: low,
    spark,
    updatedAt: Date.now(),
  }
}

/* --- majors: binance ------------------------------------------------------ */

class BinanceMajors {
  private ws: WebSocket | null = null
  private state = new Map<string, { quote: Quote; open: number }>()
  private stopped = false
  private retry = 0

  constructor(
    private onChange: () => void,
    private onFatal: () => void,
  ) {}

  /** Resolves false when Binance is unreachable, so the caller can fall back. */
  async start(): Promise<boolean> {
    this.stopped = false
    try {
      await Promise.all(
        MAJORS.map(async (m) => {
          const [tickerRes, klineRes] = await Promise.all([
            fetch(`${BINANCE_REST}/ticker/24hr?symbol=${m.binance}`),
            fetch(`${BINANCE_REST}/klines?symbol=${m.binance}&interval=15m&limit=96`),
          ])
          if (!tickerRes.ok || !klineRes.ok) throw new Error('binance rest')
          const t = await tickerRes.json()
          const k: unknown[][] = await klineRes.json()
          this.state.set(m.binance, {
            open: Number(t.openPrice),
            quote: quoteOf(
              m.symbol,
              Number(t.lastPrice),
              Number(t.openPrice),
              Number(t.highPrice),
              Number(t.lowPrice),
              k.map((row) => Number(row[4])),
            ),
          })
        }),
      )
    } catch {
      return false
    }
    this.onChange()
    this.connect()
    return true
  }

  stop() {
    this.stopped = true
    this.ws?.close()
    this.ws = null
  }

  /** Fixed order: the rail must not re-sort itself because a fetch was slow. */
  quotes(): Quote[] {
    return MAJORS.map((m) => this.state.get(m.binance)?.quote).filter((q): q is Quote => !!q)
  }

  marks(): Record<string, number> {
    const out: Record<string, number> = {}
    for (const [, s] of this.state) out[s.quote.symbol] = s.quote.price
    return out
  }

  private connect() {
    if (this.stopped) return
    const streams = MAJORS.map((m) => `${m.binance.toLowerCase()}@miniTicker`).join('/')
    const ws = new WebSocket(`${BINANCE_WS}?streams=${streams}`)
    this.ws = ws

    ws.onopen = () => {
      this.retry = 0
    }
    ws.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data as string).data
        const prev = this.state.get(d?.s)
        if (!prev) return
        const price = Number(d.c)
        const open = Number(d.o) || prev.open
        const spark = prev.quote.spark.slice()
        spark[spark.length - 1] = price
        this.state.set(d.s, {
          open,
          quote: {
            ...prev.quote,
            price,
            change24hPct: open > 0 ? (price / open - 1) * 100 : prev.quote.change24hPct,
            high24h: Math.max(prev.quote.high24h, Number(d.h) || price),
            low24h: Math.min(prev.quote.low24h || price, Number(d.l) || price),
            spark,
            updatedAt: Date.now(),
          },
        })
        this.onChange()
      } catch {
        /* one malformed frame is not worth tearing the socket down for */
      }
    }
    ws.onerror = () => ws.close()
    ws.onclose = () => {
      if (this.stopped) return
      this.retry += 1
      if (this.retry > 4) return this.onFatal()
      window.setTimeout(() => this.connect(), Math.min(30_000, RUNTIME.live.reconnectBaseMs * 2 ** this.retry))
    }
  }
}

/* --- majors: coinbase fallback -------------------------------------------- */

class CoinbaseMajors {
  private timer: number | null = null
  private state = new Map<string, Quote>()

  constructor(private onChange: () => void) {}

  async start() {
    await Promise.all(MAJORS.map((m) => this.seed(m.symbol, m.coinbase)))
    this.onChange()
    this.timer = window.setInterval(() => void this.refresh(), 4_000)
  }

  stop() {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
  }

  quotes(): Quote[] {
    return MAJORS.map((m) => this.state.get(m.symbol)).filter((q): q is Quote => !!q)
  }

  marks(): Record<string, number> {
    const out: Record<string, number> = {}
    for (const q of this.state.values()) out[q.symbol] = q.price
    return out
  }

  private async seed(symbol: string, product: string) {
    try {
      const [statsRes, candleRes] = await Promise.all([
        fetch(`${COINBASE_REST}/products/${product}/stats`),
        fetch(`${COINBASE_REST}/products/${product}/candles?granularity=900`),
      ])
      const stats = await statsRes.json()
      const candles: number[][] = await candleRes.json()
      // Coinbase returns [time, low, high, open, close, volume], newest first.
      const spark = candles.slice(0, 96).reverse().map((c) => c[4])
      this.state.set(
        symbol,
        quoteOf(
          symbol,
          Number(stats.last),
          Number(stats.open),
          Number(stats.high),
          Number(stats.low),
          spark,
        ),
      )
    } catch (err) {
      console.warn(`[utc] coinbase seed failed for ${product}`, err)
    }
  }

  private async refresh() {
    await Promise.all(
      MAJORS.map(async (m) => {
        const prev = this.state.get(m.symbol)
        if (!prev) return
        try {
          const res = await fetch(`${COINBASE_REST}/products/${m.coinbase}/ticker`)
          const t = await res.json()
          const price = Number(t.price)
          if (!Number.isFinite(price)) return
          const spark = prev.spark.slice()
          spark[spark.length - 1] = price
          const open = prev.change24hPct !== 0 ? prev.price / (1 + prev.change24hPct / 100) : prev.price
          this.state.set(m.symbol, {
            ...prev,
            price,
            change24hPct: open > 0 ? (price / open - 1) * 100 : prev.change24hPct,
            high24h: Math.max(prev.high24h, price),
            low24h: Math.min(prev.low24h || price, price),
            spark,
            updatedAt: Date.now(),
          })
        } catch {
          /* a dropped poll is recoverable; the next one is 4s away */
        }
      }),
    )
    this.onChange()
  }
}

/* --- base ecosystem index -------------------------------------------------- */

class BaseMarks {
  private timer: number | null = null
  private prices: Record<string, number> = {}

  constructor(private onChange: () => void) {}

  start() {
    void this.poll()
    this.timer = window.setInterval(() => void this.poll(), 20_000)
  }

  stop() {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
  }

  marks(): Record<string, number> {
    return this.prices
  }

  private async poll() {
    try {
      const legs = await Promise.all(BASE_LIVE_TOKENS.map((sym) => this.price(sym)))
      const ok = legs.filter((l): l is { symbol: string; price: number } => l !== null)
      if (!ok.length) return
      for (const l of ok) this.prices[l.symbol] = l.price
      this.onChange()
    } catch (err) {
      console.warn('[utc] base price poll failed', err)
    }
  }

  /** Deepest Base pair for a symbol wins: it is the one size can actually use. */
  private async price(symbol: string): Promise<{ symbol: string; price: number } | null> {
    const res = await fetch(`${DEXSCREENER}?q=${encodeURIComponent(symbol)}`)
    const json = await res.json()
    const pairs = (json.pairs ?? []).filter(
      (p: any) =>
        p.chainId === 'base' && String(p.baseToken?.symbol).toUpperCase() === symbol.toUpperCase(),
    )
    if (!pairs.length) return null
    pairs.sort((a: any, b: any) => (b.liquidity?.usd ?? 0) - (a.liquidity?.usd ?? 0))
    const price = Number(pairs[0].priceUsd)
    if (!Number.isFinite(price) || price <= 0) return null
    return { symbol, price }
  }
}

/* --- adapters -------------------------------------------------------------- */

class CompositeFeed implements PriceFeed {
  private binance: BinanceMajors | null = null
  private coinbase: CoinbaseMajors | null = null
  private base: BaseMarks | null = null
  private emit: ((u: PriceUpdate) => void) | null = null
  private source = 'binance + dexscreener'

  start(onUpdate: (u: PriceUpdate) => void) {
    this.emit = onUpdate
    const push = () => {
      const majors = this.binance ?? this.coinbase
      this.emit?.({
        quotes: majors?.quotes() ?? [],
        marks: { ...(majors?.marks() ?? {}), ...(this.base?.marks() ?? {}) },
        source: this.source,
      })
    }

    const toCoinbase = () => {
      if (this.coinbase) return
      console.warn('[utc] binance unreachable, falling back to coinbase')
      this.binance?.stop()
      this.binance = null
      this.source = 'coinbase + dexscreener'
      this.coinbase = new CoinbaseMajors(push)
      void this.coinbase.start()
    }

    this.binance = new BinanceMajors(push, toCoinbase)
    void this.binance.start().then((ok) => {
      if (!ok) toCoinbase()
    })

    this.base = new BaseMarks(push)
    this.base.start()
  }

  stop() {
    this.binance?.stop()
    this.coinbase?.stop()
    this.base?.stop()
    this.binance = null
    this.coinbase = null
    this.base = null
  }
}

/** Any endpoint returning `{ quotes, marks }` or a bare `Quote[]`. Polled. */
class CustomFeed implements PriceFeed {
  private timer: number | null = null

  constructor(private url: string) {}

  start(onUpdate: (u: PriceUpdate) => void) {
    const poll = async () => {
      try {
        const res = await fetch(this.url)
        const json = await res.json()
        const quotes: Quote[] = Array.isArray(json) ? json : (json.quotes ?? [])
        const marks: Record<string, number> = Array.isArray(json)
          ? Object.fromEntries(quotes.map((q) => [q.symbol, q.price]))
          : (json.marks ?? {})
        onUpdate({ quotes, marks, source: 'custom' })
      } catch (err) {
        console.warn('[utc] custom price feed failed', err)
      }
    }
    void poll()
    this.timer = window.setInterval(() => void poll(), 5_000)
  }

  stop() {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
  }
}

export function createPriceFeed(): PriceFeed | null {
  switch (RUNTIME.live.priceFeed) {
    case 'binance':
      return new CompositeFeed()
    case 'custom':
      return RUNTIME.live.priceFeedUrl ? new CustomFeed(RUNTIME.live.priceFeedUrl) : null
    default:
      return null
  }
}
